import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { CurrentUser, DataRoomDiligenceRequest, DiligenceRequestLink } from '../../shared/models';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getResponseData } from '../../shared/utils/axios';

interface DeleteRequestLinkDialogProps {
	dataRoomGuid: string | undefined;
	request: DataRoomDiligenceRequest | null;
	link: DiligenceRequestLink | null;
	currentUser: CurrentUser;
	close: () => void;
}

export const DeleteRequestLinkDialog: React.FC<DeleteRequestLinkDialogProps> = ({
	dataRoomGuid,
	request,
	link,
	currentUser,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onRemoveConfirmation = () => {
		if (request !== null) removeRequest(request);
	};

	const { mutate: removeRequest } = useMutation<void, unknown, DataRoomDiligenceRequest>(
		() =>
			getResponseData(
				api.delete<void>(
					CerebroApiEndpoints.DataRoomDiligenceRequests.SpecificLink(dataRoomGuid, request?.guid, link?.guid)
				)
			),
		{
			onSuccess: () => {
				if (currentUser.isBankUser) {
					client.invalidateQueries(QueryKeys.DataRoomDiligenceRequests.Lender(dataRoomGuid));
				} else {
					client.invalidateQueries(QueryKeys.DataRoomDiligenceRequests.Root(dataRoomGuid));
				}

				snackbar.enqueueSnackbar('Diligence Request Documetn Link Deleted.', { variant: 'success' });
				close();
			},
			onError: () => {
				snackbar.enqueueSnackbar('There was a problem deleting the diligence request document link.', {
					variant: 'error',
				});
				close();
			},
		}
	);

	return (
		<Dialog open={link !== null} onClose={close}>
			<DialogTitle>Confirm Delete</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to delete this diligence request document link? This will not delete the
					document from the document repository.
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onRemoveConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
